import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisible?: number;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  maxVisible = 5,
}: PaginationProps) {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    
    if (totalPages <= maxVisible) {
      // すべてのページを表示
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 省略記号を含めて表示
      const halfVisible = Math.floor(maxVisible / 2);
      
      // 最初のページは常に表示
      pages.push(1);
      
      // 中間部分
      const startPage = Math.max(2, currentPage - halfVisible);
      const endPage = Math.min(totalPages - 1, currentPage + halfVisible);
      
      // 左側に省略記号が必要か
      if (startPage > 2) {
        pages.push('...');
      }
      
      // 中間ページ
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // 右側に省略記号が必要か
      if (endPage < totalPages - 1) {
        pages.push('...');
      }
      
      // 最後のページは常に表示
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className="flex items-center justify-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        leftIcon={<ChevronLeft className="w-4 h-4" />}
      >
        前へ
      </Button>

      <div className="flex items-center gap-1">
        {pages.map((page, index) =>
          typeof page === 'number' ? (
            <button
              key={`page-${page}`}
              onClick={() => onPageChange(page)}
              className={`min-w-[2.5rem] rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                page === currentPage
                  ? 'bg-admin-accent text-white'
                  : 'text-admin-ink hover:bg-admin-bg'
              }`}
            >
              {page}
            </button>
          ) : (
            <span
              key={`ellipsis-${index}`}
              className="px-2 text-admin-muted"
            >
              {page}
            </span>
          )
        )}
      </div>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        rightIcon={<ChevronRight className="w-4 h-4" />}
      >
        次へ
      </Button>
    </div>
  );
}
